import { useState, useEffect, useMemo } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Printer, TestTube, Baby, FlaskConical, Search, X, QrCode, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import DateInput from './DateInput';

// Lista de exames organizada por categoria
const EXAMS_LIST = {
  publico_geral: {
    "Hematologia": [
      "Hemograma completo",
      "Coagulograma",
      "Eletroforese de hemoglobina"
    ],
    "Glicemia e Metabolismo": [
      "Glicemia em jejum",
      "Hemoglobina glicada",
      "Curva glicêmica em 2 tempos"
    ],
    "Perfil Lipídico": [
      "Colesterol total + frações",
      "Triglicerídeos"
    ],
    "Função Renal": [
      "Creatinina",
      "Ureia",
      "Microalbuminúria",
      "Urina 24 horas"
    ],
    "Urina e Fezes": [
      "Urina rotina",
      "Urocultura (antibiograma + cultura de bactérias)",
      "Parasitológico de fezes",
      "Pesquisa de sangue oculto nas fezes 3 amostras"
    ],
    "Eletrólitos e Minerais": [
      "Sódio",
      "Potássio",
      "Cálcio",
      "Fósforo"
    ],
    "Metabolismo do Ferro": [
      "Ferro sérico",
      "Ferritina"
    ],
    "Endocrinologia": [
      "TSH + T4 livre"
    ],
    "Função Hepática": [
      "TGO",
      "TGP",
      "Gama GT",
      "Fosfatase alcalina",
      "Bilirrubina total + frações"
    ],
    "Inflamação / Imunologia": [
      "Fator reumatoide"
    ],
    "Outros": [
      "Ácido úrico",
      "Desidrogenase lática (DHL)",
      "Cortisol",
      "RNI",
      "TAP (tempo de atividade da protrombina)",
      "PSA total e livre",
      "BHCG",
      "BAAR (3 amostras)",
      "Pesquisa de anticorpos IgG e IgM antitoxoplasma"
    ]
  },
  complementares: {
    "Exames Complementares (Não Padrão SUS)": [
      "Vitamina D (25-OH)",
      "Vitamina B12",
      "Ácido fólico",
      "Insulina",
      "Peptídeo C",
      "Kato-Katz",
      "PCR ultrassensível"
    ]
  }
};

// Estrutura da aba Gestante - Organizada por trimestre (Ministério da Saúde)
const GESTANTE_TRIMESTRES = [
  {
    id: 'primeiro',
    titulo: 'PRIMEIRO TRIMESTRE',
    subtitulo: 'Até 13 semanas e 6 dias',
    color: 'bg-pink-50 border-pink-200',
    dotColor: 'bg-pink-500',
    headerColor: 'text-pink-800',
    subColor: 'text-pink-600',
    notas: [],
    exames: [
      { id: 'gestante_primeiro_hemograma', nome: 'Hemograma completo' },
      { id: 'gestante_primeiro_abo_rh', nome: 'Tipagem sanguínea ABO e fator Rh' },
      { id: 'gestante_primeiro_coombs', nome: 'Coombs indireto' },
      { id: 'gestante_primeiro_glicemia', nome: 'Glicemia de jejum' },
      { id: 'gestante_primeiro_hiv', nome: 'Teste rápido para HIV' },
      { id: 'gestante_primeiro_vdrl', nome: 'VDRL' },
      { id: 'gestante_primeiro_hepatite_b', nome: 'Teste rápido para Hepatite B' },
      { id: 'gestante_primeiro_hepatite_c', nome: 'Teste rápido para Hepatite C' },
      { id: 'gestante_primeiro_toxoplasmose', nome: 'Toxoplasmose IgG e IgM' },
      { id: 'gestante_primeiro_htlv', nome: 'HTLV I/II' },
      { id: 'gestante_primeiro_eas', nome: 'EAS' },
      { id: 'gestante_primeiro_urocultura', nome: 'Urocultura' },
      { id: 'gestante_primeiro_eletroforese', nome: 'Eletroforese de Hemoglobina', obs: 'quando houver indicação clínica' },
    ]
  },
  {
    id: 'segundo',
    titulo: 'SEGUNDO TRIMESTRE',
    subtitulo: '24 a 28 semanas',
    color: 'bg-violet-50 border-violet-200',
    dotColor: 'bg-violet-500',
    headerColor: 'text-violet-800',
    subColor: 'text-violet-600',
    notas: [
      'Coombs indireto apenas para gestantes Rh negativo.',
      'Toxoplasmose apenas para gestantes suscetíveis.'
    ],
    exames: [
      { id: 'gestante_segundo_hemograma', nome: 'Hemograma completo' },
      { id: 'gestante_segundo_totg75', nome: 'Teste Oral de Tolerância à Glicose – TOTG 75 g' },
      { id: 'gestante_segundo_hiv', nome: 'Teste rápido para HIV' },
      { id: 'gestante_segundo_vdrl', nome: 'VDRL' },
      { id: 'gestante_segundo_hepatite_b', nome: 'Teste rápido para Hepatite B' },
      { id: 'gestante_segundo_hepatite_c', nome: 'Teste rápido para Hepatite C' },
      { id: 'gestante_segundo_eas', nome: 'EAS' },
      { id: 'gestante_segundo_urocultura', nome: 'Urocultura' },
      { id: 'gestante_segundo_toxoplasmose', nome: 'Toxoplasmose IgG e IgM', obs: 'somente para gestantes suscetíveis' },
      { id: 'gestante_segundo_coombs', nome: 'Coombs indireto', obs: 'somente para gestantes Rh negativo' },
    ]
  },
  {
    id: 'terceiro',
    titulo: 'TERCEIRO TRIMESTRE',
    subtitulo: '28 a 36 semanas',
    color: 'bg-amber-50 border-amber-200',
    dotColor: 'bg-amber-500',
    headerColor: 'text-amber-800',
    subColor: 'text-amber-600',
    notas: [
      'Cultura para Streptococcus do Grupo B deve ser coletada entre 35 e 37 semanas.',
      'Repetir toxoplasmose apenas nas pacientes suscetíveis.'
    ],
    exames: [
      { id: 'gestante_terceiro_hemograma', nome: 'Hemograma completo' },
      { id: 'gestante_terceiro_hiv', nome: 'Teste rápido para HIV' },
      { id: 'gestante_terceiro_sifilis', nome: 'Sífilis (Teste rápido e/ou VDRL)' },
      { id: 'gestante_terceiro_hepatite_b', nome: 'Teste rápido para Hepatite B' },
      { id: 'gestante_terceiro_hepatite_c', nome: 'Teste rápido para Hepatite C' },
      { id: 'gestante_terceiro_eas', nome: 'EAS' },
      { id: 'gestante_terceiro_urocultura', nome: 'Urocultura' },
      { id: 'gestante_terceiro_coombs', nome: 'Coombs indireto', obs: 'gestantes Rh negativo' },
      { id: 'gestante_terceiro_toxoplasmose', nome: 'Repetição da sorologia para toxoplasmose', obs: 'gestantes suscetíveis' },
      { id: 'gestante_terceiro_streptococcus_b', nome: 'Cultura para Streptococcus do Grupo B', obs: 'realizar entre 35 e 37 semanas' },
    ]
  }
];

// Mapeamento de ID -> nome para uso na impressão e armazenamento
const GESTANTE_ID_MAP = {};
const GESTANTE_TRIMESTRE_MAP = {};
GESTANTE_TRIMESTRES.forEach(tri => {
  tri.exames.forEach(ex => {
    GESTANTE_ID_MAP[ex.id] = ex.nome;
    GESTANTE_TRIMESTRE_MAP[ex.id] = tri.titulo;
  });
});

export default function ExamRequestModal({ open, onClose }) {
  const [patientName, setPatientName] = useState('');
  const [patientBirthDate, setPatientBirthDate] = useState('');
  const [patientSex, setPatientSex] = useState('');
  const [selectedExams, setSelectedExams] = useState([]);
  const [customExams, setCustomExams] = useState('');
  const [observations, setObservations] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('publico_geral');
  const [canGenerateQRCode, setCanGenerateQRCode] = useState(false); // Permissão de QR Code
  const [showQRCode, setShowQRCode] = useState(true); // Toggle QR Code
  const [openTrimestres, setOpenTrimestres] = useState({ primeiro: true, segundo: true, terceiro: true });
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  const currentDate = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  // Verificar permissão para gerar QR Code ao abrir modal
  useEffect(() => {
    if (open) {
      checkQRCodePermission();
    }
  }, [open]);

  const checkQRCodePermission = async () => {
    try {
      const response = await axiosInstance.get('/auth/can-generate-qrcode');
      setCanGenerateQRCode(response.data.can_generate);
      // Se não tem permissão, desabilita QR Code por padrão
      if (!response.data.can_generate) {
        setShowQRCode(false);
      } else {
        setShowQRCode(true); // Reset para true quando tem permissão
      }
    } catch (error) {
      console.error('Erro ao verificar permissão de QR Code:', error);
      setCanGenerateQRCode(false);
      setShowQRCode(false);
    }
  };

  // Função para obter lista de exames personalizados
  const getCustomExamsList = () => {
    if (!customExams.trim()) return [];
    // Suporta vírgulas ou quebras de linha como separadores
    return customExams
      .split(/[,\n]/)
      .map(exam => exam.trim())
      .filter(exam => exam.length > 0);
  };

  // Conta total de exames (selecionados + personalizados)
  const getTotalExamsCount = () => {
    return selectedExams.length + getCustomExamsList().length;
  };

  // Filtrar exames por busca
  const filterExams = (exams, search) => {
    if (!search) return exams;
    const filtered = {};
    const searchLower = search.toLowerCase();
    
    for (const [category, examList] of Object.entries(exams)) {
      const matchingExams = examList.filter(exam => 
        exam.toLowerCase().includes(searchLower)
      );
      if (matchingExams.length > 0) {
        filtered[category] = matchingExams;
      }
    }
    return filtered;
  };

  const toggleExam = (examName) => {
    setSelectedExams(prev => {
      if (prev.includes(examName)) {
        return prev.filter(e => e !== examName);
      }
      return [...prev, examName];
    });
  };

  // Verifica se um ID de exame de gestante está selecionado
  const isGestanteExamSelected = (examId) => selectedExams.includes(examId);

  // Toggle exame de gestante (usa ID como identificador)
  const toggleGestanteExam = (examId) => {
    setSelectedExams(prev => {
      if (prev.includes(examId)) {
        return prev.filter(e => e !== examId);
      }
      return [...prev, examId];
    });
  };

  // Retorna os nomes de exibição para impressão
  const getExamsForPrint = () => {
    const regular = selectedExams.filter(e => !e.startsWith('gestante_'));
    const gestanteIds = selectedExams.filter(e => e.startsWith('gestante_'));
    return { regular, gestanteIds };
  };

  // Gera HTML dos exames agrupados por trimestre para impressão
  const buildGestantePrintHtml = (gestanteIds) => {
    if (gestanteIds.length === 0) return '';

    const byTrimester = {};
    gestanteIds.forEach(id => {
      const trimestre = GESTANTE_TRIMESTRE_MAP[id];
      if (!byTrimester[trimestre]) byTrimester[trimestre] = [];
      byTrimester[trimestre].push(GESTANTE_ID_MAP[id]);
    });

    const order = ['PRIMEIRO TRIMESTRE', 'SEGUNDO TRIMESTRE', 'TERCEIRO TRIMESTRE'];
    let html = '';
    let examIndex = 1;

    order.forEach(tri => {
      if (!byTrimester[tri]) return;
      html += `<div style="margin-top: 12px; margin-bottom: 4px;">
        <p style="font-weight: bold; font-size: 11px; text-transform: uppercase; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 4px;">${tri}</p>
      </div>`;
      byTrimester[tri].forEach(nome => {
        html += `<p style="margin: 3px 0; font-size: 14px;">${examIndex}) ${nome}</p>`;
        examIndex++;
      });
    });

    return { html, nextIndex: examIndex };
  };

  const handlePrint = async () => {
    if (!patientName.trim()) {
      toast.error('Informe o nome do paciente');
      return;
    }
    
    const customExamsList = getCustomExamsList();
    const allExams = [...selectedExams, ...customExamsList];
    
    // Para envio ao backend, converter IDs de gestante para nomes de exibição
    const allExamsForBackend = [
      ...selectedExams.map(e => GESTANTE_ID_MAP[e] || e),
      ...customExamsList
    ];
    
    if (allExamsForBackend.length === 0) {
      toast.error('Selecione ou digite pelo menos um exame');
      return;
    }
    
    let examRequestId = null;
    let validationCode = null;
    
    // Criar solicitação de exame para QR Code APENAS se usuário tem permissão E toggle está ativado
    if (canGenerateQRCode && showQRCode) {
      try {
        const response = await axiosInstance.post('/exam-requests', {
          patient_name: patientName,
          patient_birth_date: patientBirthDate,
          patient_sex: patientSex,
          selected_exams: allExamsForBackend,
          custom_exams: customExams,
          observations: observations,
          unit_id: currentUser.unit_id,
          created_by_id: currentUser.id,
          created_by_name: currentUser.nome
        });
        examRequestId = response.data.id;
        validationCode = response.data.validation_code;
      } catch (error) {
        console.error('Erro ao criar solicitação de exame:', error);
        // Continua sem QR Code se falhar
      }
    }
    
    // Registrar exames para estatísticas (mantendo compatibilidade)
    try {
      await axiosInstance.post('/exams/register', {
        patient_name: patientName,
        patient_birth_date: patientBirthDate,
        patient_sex: patientSex,
        selected_exams: allExamsForBackend,
        observations: observations,
        unit_id: currentUser.unit_id
      }, {
        params: {
          user_id: currentUser.id,
          user_name: currentUser.nome
        }
      });
    } catch (error) {
      console.error('Erro ao registrar estatísticas:', error);
    }
    
    // URL de validação - Só gera se tem permissão E toggle ativado
    const validationUrl = (canGenerateQRCode && showQRCode && examRequestId) ? `${window.location.origin}/validar-exame/${examRequestId}` : null;
    
    // QR Code HTML - posicionado no footer para ficar na mesma página
    const qrCodeHtml = (canGenerateQRCode && showQRCode && validationUrl && validationCode) ? `
      <div style="text-align: right; margin-top: 10px;">
        <p style="font-size: 10px; color: #666; margin-bottom: 4px;">Validar solicitação:</p>
        <p style="font-family: monospace; font-weight: bold; font-size: 11px;">${validationCode}</p>
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=70x70&data=${encodeURIComponent(validationUrl)}" 
             alt="QR Code" style="width: 70px; height: 70px; margin-top: 4px;" />
      </div>
    ` : '';
    
    // Criar janela de impressão
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Separar exames regulares dos de gestante
    const { regular, gestanteIds } = getExamsForPrint();
    
    // Construir HTML dos exames regulares
    let regularHtml = '';
    let idx = 1;
    regular.forEach(exam => {
      regularHtml += `<p style="margin: 3px 0; font-size: 14px;">${idx}) ${exam}</p>`;
      idx++;
    });
    
    // Construir HTML dos exames de gestante (agrupados por trimestre)
    let gestanteHtml = '';
    if (gestanteIds.length > 0) {
      const result = buildGestantePrintHtml(gestanteIds);
      gestanteHtml = result.html;
    }
    
    // Exames personalizados
    const customIdx = idx;
    const customHtml = customExamsList.map((exam, i) => 
      `<p style="margin: 3px 0; font-size: 14px;">${customIdx + i}) ${exam}</p>`
    ).join('');
    
    const observationsHtml = observations ? `
      <div style="margin-top: 20px;">
        <p style="font-weight: bold; margin-bottom: 5px;">OBSERVAÇÕES CLÍNICAS:</p>
        <p style="font-size: 14px;">${observations}</p>
      </div>
    ` : '';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Solicitação de Exames - ${patientName}</title>
        <style>
          * {
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            position: relative;
            padding: 8mm;
            max-width: 210mm;
            margin: 0 auto;
            font-size: 11px;
          }
          .header {
            text-align: center;
            border-bottom: 1.5px solid black;
            padding-bottom: 6px;
            margin-bottom: 10px;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
          }
          .header img {
            width: 48px;
            height: 48px;
            object-fit: contain;
          }
          .header-text {
            flex: 1;
            margin: 0 8px;
          }
          .header-text h1 {
            font-size: 10px;
            font-weight: bold;
            margin: 0 0 1px 0;
          }
          .header-text h2 {
            font-size: 9px;
            font-weight: bold;
            margin: 0 0 1px 0;
          }
          .header-text p {
            font-size: 8px;
            margin: 0;
            line-height: 1.2;
          }
          .title {
            text-align: center;
            margin-bottom: 10px;
          }
          .title h2 {
            font-size: 14px;
            text-decoration: underline;
            text-transform: uppercase;
            margin: 0;
          }
          .patient {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .date {
            font-size: 11px;
            margin-bottom: 10px;
          }
          .exams-title {
            font-weight: bold;
            text-decoration: underline;
            margin-bottom: 6px;
            font-size: 11px;
          }
          .exams {
            margin-bottom: 10px;
          }
          .exams ul {
            margin: 0;
            padding-left: 20px;
            columns: 2;
            column-gap: 20px;
          }
          .exams li {
            font-size: 10px;
            line-height: 1.3;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid black;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }
          .signature {
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid black;
            width: 180px;
            margin-bottom: 3px;
          }
          @media print {
            @page {
              size: A4;
              margin: 8mm;
            }
            body { 
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .header img {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            /* Evitar quebra de página */
            * {
              page-break-inside: avoid;
            }
            body {
              page-break-after: avoid;
              page-break-before: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${window.location.origin}/assets/brasao-sao-domingos.png" 
               alt="Brasão" 
               onerror="this.onerror=null; this.src='https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Brasao-saodomingosdoprata-mg.jpg/120px-Brasao-saodomingosdoprata-mg.jpg';">
          <div class="header-text">
            <h1>Fundo Municipal de Saúde de São Domingos do Prata</h1>
            <h2>SECRETARIA MUNICIPAL DE SAÚDE E AÇÃO SOCIAL</h2>
            <p>Rua Prof. Cristiano Morais, nº 38-Centro São Domingos do Prata-MG</p>
            <p>Telefone: (31) 3868-1310</p>
            <p>E-mail: dmsac@saodomingosdoprata.mg.gov.br</p>
            <p>CNPJ: 11.466.385/0001-68-Inscrição Estadual: Isento</p>
          </div>
          <img src="${window.location.origin}/assets/logo-sus.png" 
               alt="SUS" 
               onerror="this.onerror=null; this.src='https://upload.wikimedia.org/wikipedia/commons/thumb/3/39/Logo_SUS.svg/120px-Logo_SUS.svg.png';">
        </div>
        
        <div class="title">
          <h2>SOLICITAÇÃO DE EXAMES</h2>
        </div>
        
        <div class="patient">
          PACIENTE: ${patientName.toUpperCase()}
        </div>
        
        <div class="date">
          <strong>Data:</strong> ${currentDate}
        </div>
        
        <div class="exams">
          <p class="exams-title">EXAMES SOLICITADOS:</p>
          ${regularHtml}
          ${gestanteHtml}
          ${customHtml}
        </div>
        
        ${observationsHtml}
        
        <div class="footer">
          <div>
            <p style="font-weight: bold; font-size: 14px;">São Domingos do Prata</p>
          </div>
          <div class="signature">
            <div class="signature-line"></div>
            <p style="font-size: 12px;">Assinatura e Carimbo do Profissional</p>
          </div>
          <div>
            <p style="font-weight: bold; font-size: 14px;">${currentDate}</p>
            ${qrCodeHtml}
          </div>
        </div>
        
        <script>
          // Aguardar carregamento das imagens antes de mostrar a página
          function checkImagesAndPrint() {
            var images = document.querySelectorAll('img');
            var loaded = 0;
            var total = images.length;
            
            if (total === 0) {
              window.print();
              return;
            }
            
            images.forEach(function(img) {
              if (img.complete) {
                loaded++;
                if (loaded === total) window.print();
              } else {
                img.onload = function() {
                  loaded++;
                  if (loaded === total) window.print();
                };
                img.onerror = function() {
                  loaded++;
                  if (loaded === total) window.print();
                };
              }
            });
          }
          
          // Dar tempo para as imagens carregarem
          setTimeout(checkImagesAndPrint, 800);
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    toast.success('Pedido de exames gerado!');
  };

  const handleReset = () => {
    setPatientName('');
    setPatientBirthDate('');
    setPatientSex('');
    setSelectedExams([]);
    setCustomExams('');
    setObservations('');
    setSearchTerm('');
    // Manter showQRCode baseado na permissão
    if (canGenerateQRCode) {
      setShowQRCode(true);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Renderizar categoria de exames - layout compacto como no papel
  const renderExamCategory = (categoryName, exams, tabType) => (
    <div key={categoryName} className="mb-4">
      <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2 border-b pb-1">
        <div className={`w-2 h-2 rounded-full ${
          tabType === 'publico_geral' ? 'bg-teal-500' :
          tabType === 'gestante' ? 'bg-pink-500' : 'bg-purple-500'
        }`}></div>
        {categoryName}
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
        {exams.map((exam) => (
          <label 
            key={exam}
            className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors hover:bg-gray-100 ${
              selectedExams.includes(exam) ? 'bg-primary/10' : ''
            }`}
          >
            <Checkbox
              checked={selectedExams.includes(exam)}
              onCheckedChange={() => toggleExam(exam)}
              className="h-4 w-4 border-2"
            />
            <span className={`text-xs leading-tight ${
              selectedExams.includes(exam) ? 'font-medium text-primary' : 'text-gray-700'
            }`}>{exam}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="w-5 h-5 text-primary" />
              Requisição de Exames Laboratoriais
            </DialogTitle>
            <DialogDescription>
              Marque os exames desejados e clique em Imprimir Pedido
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {/* Dados do Paciente - Compacto */}
            <div className="p-3 bg-gray-50 rounded-lg mb-4 border">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Label htmlFor="patientName" className="text-xs">Nome do Paciente *</Label>
                  <Input
                    id="patientName"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value.toUpperCase())}
                    placeholder="NOME COMPLETO"
                    className="mt-1 h-9 bg-white text-sm"
                    data-testid="exam-patient-name"
                  />
                </div>
                <div>
                  <Label htmlFor="patientBirthDate" className="text-xs">Data Nascimento</Label>
                  <DateInput
                    id="patientBirthDate"
                    value={patientBirthDate}
                    onChange={setPatientBirthDate}
                    className="mt-1 h-9 bg-white text-sm"
                    data-testid="exam-patient-birth"
                  />
                </div>
                <div>
                  <Label htmlFor="patientSex" className="text-xs">Sexo</Label>
                  <Select value={patientSex} onValueChange={setPatientSex}>
                    <SelectTrigger className="mt-1 h-9 bg-white text-sm">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Busca + Exames Selecionados */}
            <div className="flex gap-4 mb-3 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar exame..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-9 text-sm"
                  data-testid="exam-search"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {selectedExams.length > 0 && (
                <Badge variant="default" className="h-7 px-3">
                  {selectedExams.length} exame(s) selecionado(s)
                </Badge>
              )}
            </div>

            {/* Tabs de Exames */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
              <TabsList className="grid w-full grid-cols-3 mb-3">
                <TabsTrigger value="publico_geral" className="flex items-center gap-2 text-xs">
                  <TestTube className="w-3 h-3" />
                  Público Geral
                </TabsTrigger>
                <TabsTrigger value="gestante" className="flex items-center gap-2 text-xs">
                  <Baby className="w-3 h-3" />
                  Gestante
                </TabsTrigger>
                <TabsTrigger value="complementares" className="flex items-center gap-2 text-xs">
                  <FlaskConical className="w-3 h-3" />
                  Complementares
                </TabsTrigger>
              </TabsList>

              <div className="border rounded-lg p-3 bg-white max-h-[350px] overflow-y-auto">
                <TabsContent value="publico_geral" className="m-0 mt-0">
                  {Object.entries(filterExams(EXAMS_LIST.publico_geral, searchTerm)).map(([category, exams]) =>
                    renderExamCategory(category, exams, 'publico_geral')
                  )}
                </TabsContent>

                <TabsContent value="gestante" className="m-0 mt-0">
                  <div className="space-y-3">
                    {GESTANTE_TRIMESTRES.map(tri => {
                      const isOpen = openTrimestres[tri.id];
                      const filteredExames = searchTerm
                        ? tri.exames.filter(ex => ex.nome.toLowerCase().includes(searchTerm.toLowerCase()))
                        : tri.exames;
                      if (searchTerm && filteredExames.length === 0) return null;
                      const selectedCount = tri.exames.filter(ex => selectedExams.includes(ex.id)).length;

                      return (
                        <div key={tri.id} className={`rounded-lg border ${tri.color} overflow-hidden`} data-testid={`trimestre-${tri.id}`}>
                          <button
                            type="button"
                            onClick={() => setOpenTrimestres(prev => ({ ...prev, [tri.id]: !prev[tri.id] }))}
                            className="w-full flex items-center justify-between px-4 py-3 hover:opacity-80 transition-opacity"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-2.5 h-2.5 rounded-full ${tri.dotColor}`} />
                              <div className="text-left">
                                <p className={`font-bold text-sm ${tri.headerColor}`}>{tri.titulo}</p>
                                <p className={`text-xs ${tri.subColor}`}>{tri.subtitulo}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {selectedCount > 0 && (
                                <Badge variant="default" className="text-xs h-5 px-2">{selectedCount}</Badge>
                              )}
                              {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                            </div>
                          </button>

                          {isOpen && (
                            <div className="px-4 pb-4 pt-1 space-y-1">
                              {filteredExames.map(ex => (
                                <label
                                  key={ex.id}
                                  className={`flex items-start gap-2.5 py-1.5 px-2 rounded cursor-pointer transition-colors hover:bg-white/60 ${
                                    selectedExams.includes(ex.id) ? 'bg-white/80' : ''
                                  }`}
                                  data-testid={`exam-${ex.id}`}
                                >
                                  <Checkbox
                                    checked={selectedExams.includes(ex.id)}
                                    onCheckedChange={() => toggleGestanteExam(ex.id)}
                                    className="h-4 w-4 border-2 mt-0.5 shrink-0"
                                  />
                                  <span className="text-xs leading-tight">
                                    <span className={selectedExams.includes(ex.id) ? 'font-medium text-primary' : 'text-gray-800'}>
                                      {ex.nome}
                                    </span>
                                    {ex.obs && (
                                      <span className="text-gray-400 italic ml-1">({ex.obs})</span>
                                    )}
                                  </span>
                                </label>
                              ))}

                              {tri.notas.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-dashed border-gray-300 space-y-0.5">
                                  {tri.notas.map((nota, i) => (
                                    <p key={i} className="text-[10px] text-gray-500 italic leading-tight pl-2">* {nota}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="complementares" className="m-0 mt-0">
                  {Object.entries(filterExams(EXAMS_LIST.complementares, searchTerm)).map(([category, exams]) =>
                    renderExamCategory(category, exams, 'complementares')
                  )}
                </TabsContent>
              </div>
            </Tabs>

            {/* Campo de Pedido Livre */}
            <div className="mt-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <FlaskConical className="w-4 h-4 text-amber-600" />
                <Label htmlFor="customExams" className="text-sm font-semibold text-amber-800">
                  Outros Exames / Pedido Livre
                </Label>
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                  Opcional
                </Badge>
              </div>
              <Textarea
                id="customExams"
                value={customExams}
                onChange={(e) => setCustomExams(e.target.value)}
                placeholder="Digite exames adicionais não listados acima...&#10;Exemplos: Vitamina B6, PCR, Amilase&#10;(Separe por vírgula ou quebra de linha)"
                className="mt-1 h-20 bg-white text-sm resize-none"
                data-testid="exam-custom-exams"
              />
              <p className="text-xs text-amber-600 mt-1">
                Estes exames aparecerão no pedido impresso junto aos selecionados acima.
              </p>
            </div>

            {/* Observações */}
            <div className="mt-3">
              <Label htmlFor="observations" className="text-xs">Observações Clínicas (opcional)</Label>
              <Textarea
                id="observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Observações..."
                className="mt-1 h-16 bg-white text-sm"
                data-testid="exam-observations"
              />
            </div>

            {/* Toggle QR Code - Só aparece se usuário tem permissão */}
            {canGenerateQRCode && (
              <div className="flex items-center justify-between mt-3 px-3 py-2 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-gray-600" />
                  <Label htmlFor="qr-toggle-exam" className="text-sm text-gray-700">
                    Incluir QR Code de validação na impressão
                  </Label>
                </div>
                <Switch
                  id="qr-toggle-exam"
                  checked={showQRCode}
                  onCheckedChange={setShowQRCode}
                />
              </div>
            )}
          </div>

          {/* Botões de Ação - fixos no rodapé */}
          <div className="flex justify-between items-center pt-3 border-t mt-3 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={handleReset}>
              Limpar
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleClose}>
                Cancelar
              </Button>
              <Button 
                size="sm"
                onClick={handlePrint}
                disabled={!patientName.trim() || (selectedExams.length === 0 && !customExams.trim())}
                className="bg-primary hover:bg-primary/90"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimir Pedido ({getTotalExamsCount()})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
